import { InputQuestion } from '../enrollment/enrollment-context';
import { CardData } from '../../../domains/confirmation/components/services/card';
import { PerformerType } from '../../constants/performer.constants';
import { Configuration as ProductConfiguration } from '../../../../hooks/useProduct';

export type ProgramSelection = {
	title: string;
	description: string;
};

export type SocialNetwork = {
	name: string;
	link: string;
};

export type Consent = {
	id: string;
	channel?: string[];
};

export type Rule = {
	id: string;
	operator?: string;
	//TODO - value any is ok as it won't cause any type issues, to be fixed in future
	value: any; //NOSONAR
};

export type ServiceConfiguration = {
	name?: string;
	consents?: { explicit: string[] };
	static?: CardData | boolean;
	elegibility?: { patient?: Rule[]; caregiver?: Rule[]; questions?: Rule[] };
	legal: boolean;
	fasttrack?: boolean;
};

export class Service {
	name: string = '';
	legal: boolean;
	consents: { explicit: string[] } = {
		explicit: [],
	};
	elegibility?: { patient?: Rule[]; caregiver?: Rule[]; questions?: Rule[] };
	static?: CardData | boolean;
	fasttrack?: boolean = false;

	constructor(configuration: ServiceConfiguration) {
		this.legal = configuration.legal;
		this.consents = configuration.consents || this.consents;
		this.name = configuration.name || this.name;
		this.static = configuration.static;
		this.fasttrack = configuration.fasttrack;
		this.elegibility = configuration.elegibility;
	}

	private checkEligibility(
		subgroup: Rule[],
		answers: InputQuestion[]
	): boolean {
		return !subgroup.some((question) => {
			const answer = answers.find(
				(currentAnswer) => currentAnswer.id === question.id
			);
			return answer && !answer.isQualified(subgroup);
		});
	}

	private explicitConsentIsMissing(consents: string[]) {
		return this.consents?.explicit?.some(
			(consent) => !consents.includes(consent)
		);
	}

	isEligible(
		performer: PerformerType.CAREGIVER | PerformerType.PATIENT,
		answers: InputQuestion[],
		consents: string[],
		discardedServices?: string[]
	): boolean {
		if (
			this.consents &&
			!discardedServices &&
			this.explicitConsentIsMissing(consents)
		) {
			return false;
		}
		if (discardedServices?.includes(this.name)) {
			return false;
		}
		if (this.elegibility) {
			const common =
				!this.elegibility.questions ||
				this.checkEligibility(this.elegibility.questions, answers);
			return (
				common &&
				(!this.elegibility[performer] ||
					this.checkEligibility(this.elegibility[performer]!, answers))
			);
		}
		return true;
	}
}

export class Program {
	title: string = '';
	services: Service[] = [];
	constructor(initialLoad?: { title?: string; services?: Service[] }) {
		if (initialLoad) {
			if (initialLoad.title) {
				this.title = initialLoad.title;
			}
			if (initialLoad.services) {
				this.services = initialLoad.services.map(
					(service) => new Service(service)
				);
			}
		}
	}
}

export class ConfigurationModel {
	flow: string = '';
	params: { [key: string]: string } = {};
	programs: Program[] = [];
	consents: { [key: string]: Consent } = {};
	territories?: string[];

	constructor(product?: ProductConfiguration) {
		if (product) {
			this.loadProduct(product);
		}
	}
	get services(): Service[] {
		return this.programs.reduce((services: Service[], program: Program) => {
			return services.concat(program.services);
		}, []);
	}

	getService(name: string): Service | undefined {
		return this.services.find((service) => service.name === name);
	}

	initializeParams(product: ProductConfiguration) {
		this.params = product.params || {};
	}

	loadProduct(product: ProductConfiguration) {
		this.initializeParams(product);
		if (product.programs) {
			const programs = product.programs.map(
				(program: Program) => new Program(program)
			);
			this.programs = programs;
		} else {
			this.programs = [];
		}
		product.consents?.forEach((item) => {
			this.consents[item.id] = item;
		});
		if(product.territories){
			this.territories=product.territories
		}
	}
}
